import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { ITreeOption } from '@/types';
import { collectUnloadedNodes, hasNestedItems, lazyLoadItems, replaceNodesChildren } from './Folders.utils';

/** Loads only permission-filtered menu endpoints; saved pins never supply their own URLs. */
export function useMenuIndex(list: ITreeOption[], setList: Dispatch<SetStateAction<ITreeOption[]>>, enabled: boolean) {
    const attempted = useRef(new Set<string>());
    const mounted = useRef(false);
    const inFlight = useRef(0);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const [retry, setRetry] = useState(0);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    useEffect(() => {
        if (!enabled || !list.length) return;
        const attemptedKeys = attempted.current;
        const candidates = collectUnloadedNodes(list).filter(
            ({ node, key }) => hasNestedItems(node) && !attemptedKeys.has(key),
        );
        if (!candidates.length) return;
        candidates.forEach(({ key }) => attemptedKeys.add(key));
        inFlight.current += 1;
        setLoading(true);
        // A wave outlives list changes: the fold is functional over whatever tree is current by then.
        void Promise.allSettled(candidates.map(({ node }) => lazyLoadItems(node))).then((results) => {
            inFlight.current -= 1;
            if (!mounted.current) return;
            setFailed((prev) => prev || results.some((result) => result.status === 'rejected'));
            const loaded = new Map<string, ITreeOption[]>();
            results.forEach((result, i) => {
                if (result.status === 'fulfilled') loaded.set(candidates[i].key, result.value);
            });
            setList((prev) => replaceNodesChildren(prev, loaded));
            setLoading(inFlight.current > 0);
        });
    }, [list, setList, enabled, retry]);
    // Постоянная ссылка: меню под memo не перерисовывается от рендера Folders.
    const retryLoading = useCallback(() => {
        attempted.current.clear();
        setFailed(false);
        setRetry((value) => value + 1);
    }, []);
    return { loading, failed, retry: retryLoading };
}
